import { unwrapMaybeOr, type Maybe } from "./maybe";

/** Locations first, characters and arc events next, then other types by name. */
export function sortSceneChips<
  T extends { kind: string; label: string; entryFile: Maybe<string> },
>(chips: readonly T[], entryTypes: ReadonlyMap<string, string>): T[] {
  function typeOf(chip: T): string {
    if (chip.kind === "arc" || chip.kind === "character") return "character";
    const type = (
      entryTypes.get(unwrapMaybeOr(chip.entryFile, "")) ?? chip.kind
    )
      .trim()
      .toLowerCase();
    if (type === "place") return "location";
    return type === "entity" || type.length === 0 ? "other" : type;
  }
  function rank(type: string): number {
    if (type === "location") return 0;
    if (type === "character") return 1;
    return 2;
  }
  const { compare } = new Intl.Collator([], { sensitivity: "base" });
  // Map previews keep their existing priority and remain separate from chips.
  return [
    ...chips.filter((chip) => chip.kind === "map"),
    ...chips
      .filter((chip) => chip.kind !== "map")
      .sort((a, b) => {
        const aType = typeOf(a);
        const bType = typeOf(b);
        const groupOrder = rank(aType) - rank(bType);
        if (groupOrder !== 0) return groupOrder;
        const typeOrder = compare(aType, bType);
        return typeOrder !== 0 ? typeOrder : compare(a.label, b.label);
      }),
  ];
}
